import { HttpClient } from '@angular/common/http';
import { Injectable, signal, inject } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, throwError } from 'rxjs';
import {
  SyncWorkflowResponse,
  WorkflowExecutionHistoryData,
  WorkflowExecutionHistoryResponse,
  WorkflowExecutionResponse,
  WorkflowItem,
  WorkflowResponse,
} from '../interfaces/api-response.interfaces';

export interface MonitoringConfig {
  apiUrl?: string | null;
}

export interface WorkflowFilters {
  q?: string;
  is_enabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  private http = inject(HttpClient);

  private selectedWorkflow = signal<WorkflowItem | null>(null);
  private workflowsSubject = new BehaviorSubject<WorkflowItem[]>([]);
  public workflows$ = this.workflowsSubject.asObservable();
  private config: MonitoringConfig | null = null;

  // Config URL de la API
  configure(config: MonitoringConfig): void {
    this.config = config;
  }

  // Obtener workflows
  getWorkflows(limit = 9, page = 1, filters?: WorkflowFilters): Observable<WorkflowResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workflows`;

    const params: Record<string, string> = {
      limit: limit.toString(),
      page: page.toString(),
    };

    const searchQuery = filters?.q?.trim();
    if (searchQuery) {
      params['q'] = searchQuery;
    }

    if (typeof filters?.is_enabled === 'boolean') {
      params['is_enabled'] = String(filters.is_enabled);
    }

    return this.http.get<WorkflowResponse>(url, { params }).pipe(
      map((response) => {
        this.workflowsSubject.next(response.data);
        return response;
      }),
      catchError((error) => {
        console.error('Error al obtener workflows:', error);
        return of({
          data: [],
          total: 0,
          limit,
          page,
        });
      }),
    );
  }

  getWorkflowExecutions(
    workflowId: number,
    limit = 10,
    page = 1,
    statuses?: string[],
    dateFrom?: Date | null,
    dateTo?: Date | null,
  ): Observable<WorkflowExecutionResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutionlists/workflow/${workflowId}/`;

    const params: Record<string, string> = {
      limit: limit.toString(),
      page: page.toString(),
    };

    // Agregar estados a los parámetros de la query si están presentes
    if (statuses && statuses.length > 0) {
      params['status'] = statuses.join(',');
    }

    // Agregar fechas a los parámetros de la query si están presentes
    if (dateFrom) {
      // Formatear fecha como YYYY-MM-DD
      const fromDate = new Date(dateFrom);
      params['start_date'] = fromDate.toISOString().split('T')[0];
    }

    if (dateTo) {
      // Formatear fecha como YYYY-MM-DD
      const toDate = new Date(dateTo);
      params['end_date'] = toDate.toISOString().split('T')[0];
    }

    return this.http.get<WorkflowExecutionResponse>(url, { params }).pipe(
      catchError((error) => {
        console.error('Error al obtener workflows id:', error);
        // En caso de error
        return of({
          data: [],
          total: 0,
          limit,
          page,
        });
      }),
    );
  }

  getExecutionHistoryById(
    executionId: number,
    limit = 10,
    page = 1,
    statuses?: string[],
  ): Observable<WorkflowExecutionHistoryResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutionlists/${executionId}/`;

    const params: Record<string, string> = {
      limit: limit.toString(),
      page: page.toString(),
    };

    // Agregar estados a los parámetros de la query si están presentes
    if (statuses && statuses.length > 0) {
      params['status'] = statuses.join(',');
    }

    return this.http.get<WorkflowExecutionHistoryResponse>(url, { params }).pipe(
      catchError((error) => {
        console.error('Error al obtener workflows id:', error);
        // En caso de error, devolver estructura vacía
        return of({
          data: [],
          total: 0,
          limit,
          page,
        });
      }),
    );
  }

  syncronizeWorkflowById(workflowId: number): Observable<SyncWorkflowResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workflow/run/${workflowId}/`;

    const body = {};

    return this.http.post<SyncWorkflowResponse>(url, body).pipe(
      catchError((error) => {
        console.error('Error al sincronizar workflow:', error);
        return throwError(() => error);
      }),
    );
  }

  getWorkflowById(workflowId: number) {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workflows/${workflowId}/`;

    return this.http.get<WorkflowItem>(url).pipe(
      catchError((error) => {
        console.error('Error al obtener workflow:', error);
        return throwError(() => error);
      }),
    );
  }

  getHistoryDetailsById(executionId: number): Observable<WorkflowExecutionHistoryData> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutions/${executionId}/`;

    return this.http.get<WorkflowExecutionHistoryData>(url).pipe(
      catchError((error) => {
        console.error('Error al obtener detalles del historial:', error);
        return throwError(() => error);
      }),
    );
  }

  selectWorkflow(workflow: WorkflowItem): void {
    this.selectedWorkflow.set(workflow);
  }

  getSelectedWorkflow(): WorkflowItem | null {
    return this.selectedWorkflow();
  }

  clearSelection(): void {
    this.selectedWorkflow.set(null);
  }

  getEnabledWorkflows(): Observable<WorkflowItem[]> {
    return this.workflows$.pipe(
      map((workflows) => workflows.filter((w) => w.periodic_task?.enabled)),
    );
  }

  getDisabledWorkflows(): Observable<WorkflowItem[]> {
    return this.workflows$.pipe(
      map((workflows) => workflows.filter((w) => !w.periodic_task?.enabled)),
    );
  }

  getWorkflowStats(): Observable<{
    total: number;
    enabled: number;
    disabled: number;
    totalRuns: number;
  }> {
    return this.workflows$.pipe(
      map((workflows) => ({
        total: workflows.length,
        enabled: workflows.filter((w) => w.periodic_task?.enabled).length,
        disabled: workflows.filter((w) => !w.periodic_task?.enabled).length,
        totalRuns: workflows.reduce((sum, w) => sum + (w.periodic_task?.total_run_count || 0), 0),
      })),
    );
  }

  private getApiUrl(): string {
    if (!this.config) {
      throw new Error('MonitoringService no está configurado. Llama a configure() primero.');
    }
    return <string>this.config.apiUrl;
  }
}
